import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display';
import { Badge } from '@/components/data-display/Badge';
import { Pagination } from '@/components/data-display/Pagination';
import { Button } from '@/components/common';
import type { IncomingPaymentDto, IncomingStatusFilter, IncomingStatusCode } from './types';
import { INCOMING_STATUS_LABELS } from './types';
import { formatAmount, formatDate, STATUS_BADGE } from './format';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IncomingListProps {
  data: IncomingPaymentDto[];
  total: number | null;
  isLoading: boolean;
  page: number;
  limit: number;
  statusFilter: IncomingStatusFilter;
  onStatusFilter: (status: IncomingStatusFilter) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  clientNameById: Map<number, string>;
  onRowClick?: (payment: IncomingPaymentDto) => void;
}

// ---------------------------------------------------------------------------
// Status filter pills
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: IncomingStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IncomingList({
  data,
  total,
  isLoading,
  page,
  limit,
  statusFilter,
  onStatusFilter,
  onPageChange,
  onPageSizeChange,
  clientNameById,
  onRowClick,
}: IncomingListProps) {
  const columns = useMemo<ColumnDef<IncomingPaymentDto, unknown>[]>(
    () => [
      {
        accessorKey: 'sender_name',
        header: 'Sender',
        cell: ({ row }) => (
          <span data-testid={`sender-${row.original.incoming_payment_id}`}>
            {row.original.sender_name ?? '—'}
          </span>
        ),
      },
      {
        id: 'receiver',
        header: 'Receiver',
        cell: ({ row }) => (
          <span data-testid={`receiver-${row.original.incoming_payment_id}`}>
            {row.original.client_name ?? clientNameById.get(row.original.client_id) ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="font-medium" data-testid={`amount-${row.original.incoming_payment_id}`}>
            {formatAmount(row.original.amount, row.original.currency_code)}
          </span>
        ),
      },
      {
        accessorKey: 'currency_code',
        header: 'Currency',
        cell: ({ row }) => row.original.currency_code,
      },
      {
        accessorKey: 'payment_status',
        header: 'Status',
        cell: ({ row }) => {
          const code = row.original.payment_status;
          const label = INCOMING_STATUS_LABELS[code as IncomingStatusCode] ?? code;
          return (
            <span data-testid={`status-${row.original.incoming_payment_id}`}>
              <Badge variant={STATUS_BADGE[code] ?? 'default'}>{label}</Badge>
            </span>
          );
        },
      },
      {
        accessorKey: 'reference',
        header: 'Reference',
        cell: ({ row }) => (
          <span className="text-[12px] text-[#999]">{row.original.reference ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
      {
        id: 'details',
        header: '',
        cell: ({ row }) => {
          const payment = row.original;
          const label = `View details for payment ${payment.transaction_id ?? payment.incoming_payment_id}`;
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick?.(payment);
                }}
                aria-label={label}
                data-testid={`details-btn-${payment.incoming_payment_id}`}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ],
    [clientNameById, onRowClick],
  );

  const hasTotal = total !== null;
  const from = (page - 1) * limit + 1;
  const to = hasTotal ? Math.min(page * limit, total) : page * limit;

  return (
    <div>
      {/* Status filter pills */}
      <div className="mb-4 flex items-center gap-2" data-testid="status-filter">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onStatusFilter(opt.value)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              statusFilter === opt.value
                ? 'bg-[#6B0F1A] text-white'
                : 'bg-[#f5f5f5] text-[#666] hover:bg-[#e8e8e8]'
            }`}
            data-testid={`filter-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      {hasTotal && total > 0 && (
        <p className="mb-3 text-[13px] text-[#999]" data-testid="pagination-summary">
          Showing {from}&ndash;{to} of {total} payments
        </p>
      )}
      {!hasTotal && data.length > 0 && (
        <p className="mb-3 text-[13px] text-[#999]" data-testid="pagination-summary">
          Showing {from}&ndash;{from + data.length - 1}
        </p>
      )}

      {/* Table — row click is for mouse users; keyboard activation uses the
          in-cell View button so the <tr> keeps its native row semantics. */}
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        rowClick={onRowClick}
        getRowProps={(row) => ({
          'data-testid': `incoming-row-${row.incoming_payment_id}`,
        })}
      />

      {/* Pagination — suppressed while backend does not return totals */}
      {hasTotal && total > 0 && (
        <Pagination
          pageIndex={page}
          pageSize={limit}
          totalRows={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          className="mt-2"
        />
      )}
    </div>
  );
}
